/**
 * ``redactEvent()`` — PostHog ``before_send`` hook.
 *
 * Runs on every event before posthog-js dispatches it. Three jobs,
 * applied uniformly to every event regardless of name:
 *
 *   1. **Strip query strings + fragments** from any URL-shaped string
 *      anywhere in the payload. The headline risk is auto-captured
 *      ``$exception`` events: posthog-js attaches ``$current_url`` and
 *      walks ``error.stack`` for source URLs, both of which may carry
 *      ``?token=...`` or ``?debug=secret`` from the page URL. The
 *      catalog-validated explicit events from ``captureEvent()`` never
 *      contain URLs, but this hook runs on auto-capture too — so the
 *      strip applies everywhere.
 *
 *   2. **Drop forbidden properties** anywhere in the payload (top
 *      level + nested objects). Belt-and-braces against the
 *      ``captureEvent()`` fence which only sees explicit events;
 *      ``$exception`` is auto-captured by posthog-js with whatever
 *      properties the SDK chose to attach. The forbidden-property
 *      list lives in ``governance/forbidden.ts`` so this redactor
 *      and the capture wrapper share one truth source.
 *
 *   3. **Truncate any string property longer than 500 chars** —
 *      matches the backend wrapper's per-property cap (Plan_03
 *      § "Backend redaction policy") so a runaway error message
 *      doesn't bloat PostHog storage or carry tail data we didn't
 *      intend to send.
 *
 * Pure function. ``redactEvent(null)`` returns ``null`` so a posthog
 * upstream hook that already dropped the event keeps it dropped.
 *
 * Plan anchors
 * ------------
 * - ``plans/PLAN_POSTHOG_03_OBSERVABILITY.md`` § "What goes into the
 *   exception payload" and § "PostHog Exceptions — frontend"
 * - ``plans/PLAN_POSTHOG_05_GOVERNANCE.md`` § "Forbidden property
 *   names"
 * - Resolves Plan_03 open question 03.Q2 — yes, redactor strips
 *   query strings and URL fragments before send.
 */

import {
  isForbiddenProperty,
  isPersonPropertyAllowed,
} from "@/lib/analytics/governance/forbidden";

/**
 * Keys posthog-js uses internally inside ``properties`` that the
 * redactor MUST NOT strip — even though they collide with our
 * forbidden-property list.
 *
 * The headline case is ``properties.token``: posthog-js attaches the
 * project API key here (not at the request body root) before sending
 * to ``/i/v0/e/``. Our forbidden list contains ``token`` to catch
 * user-supplied auth tokens (CSRF, JWT, etc.). If the redactor drops
 * ``properties.token``, the request goes out without an api_key and
 * PostHog ingest returns 401 "event submitted without an api_key" —
 * see https://github.com/PostHog/posthog-js/issues/3438.
 *
 * The user-event path is unaffected: ``captureEvent()`` validates
 * payloads against a discriminated-union Zod schema that rejects
 * unknown properties, so a developer cannot accidentally send a
 * ``token`` field through that wrapper. The redactor's broader fence
 * is what was overreaching.
 */
const POSTHOG_INTERNAL_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  "token",
]);

/**
 * Property keys whose VALUE is a person/group property bag. posthog-js
 * nests these inside ``properties`` on ``$identify`` / ``$groupidentify``
 * events (e.g. ``properties.$set``, ``properties.$group_set``). Once the
 * redactor descends into one of these, the event-property forbidden
 * filter is relaxed to the person-property allowlist — ``email`` /
 * ``name`` / ``full_name`` are allowed (they BELONG on the Person), while
 * secrets stay stripped. Without this, the forbidden fence emptied every
 * ``$set`` and the Person stayed a bare distinct_id with no identity.
 */
const PERSON_PROPERTY_BAG_KEYS: ReadonlySet<string> = new Set([
  "$set",
  "$set_once",
  "$group_set",
  "$group_set_once",
]);

/**
 * Loose structural shape of the events posthog-js feeds the
 * ``before_send`` hook. The SDK's full ``CaptureResult`` requires
 * ``uuid`` + ``timestamp`` to be present, but the redactor only
 * reads ``event`` + ``properties`` (and forwards the rest). Keeping
 * a local interface lets test callers construct partial events
 * without having to fabricate a uuid + timestamp pair on every
 * fixture. The wiring in ``provider.tsx`` casts to ``BeforeSendFn``
 * at the SDK boundary.
 */
export interface RedactableEvent {
  uuid?: string;
  event: string;
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
  timestamp?: Date;
}

const STRING_MAX_LENGTH = 500;

/** Replace ``?... `` and ``#... `` tails of any URL-shape token in a string. */
function stripQueryAndFragment(input: string): string {
  // Replace any contiguous URL-like substring's query + fragment.
  // ``\bhttps?://[^\s'"<>]+`` matches absolute URLs in stack traces /
  // referrers; ``(^|\s)/[^?#\s]+`` matches bare paths like
  // ``/dashboard?country=...`` that appear in $current_url-style
  // values. We avoid regex backtracking explosions by anchoring at
  // word boundaries and stopping at whitespace.
  const withQueryRedacted = input.replace(
    /(https?:\/\/[^\s'"<>]*?|\/[^\s'"<>]*?)\?[^\s'"<>#]*/g,
    "$1?[REDACTED]",
  );
  return withQueryRedacted.replace(
    /(https?:\/\/[^\s'"<>]*?|\/[^\s'"<>]*?)#[^\s'"<>]*/g,
    "$1",
  );
}

/** Truncate a long string to the per-property cap with a tail marker. */
function truncateString(input: string): string {
  if (input.length <= STRING_MAX_LENGTH) return input;
  // ``[truncated]`` tail makes the cap visible at read time; counts
  // as part of the cap so the total stays bounded.
  const tail = "…[truncated]";
  return input.slice(0, STRING_MAX_LENGTH - tail.length) + tail;
}

/**
 * Recursively redact a value:
 *   - strings → strip URL query/fragment, then truncate
 *   - arrays  → map each element through ``redactValue``
 *   - plain objects → drop forbidden keys; redact each remaining value
 *   - everything else → return as-is
 *
 * ``Date`` / ``null`` / numbers / booleans pass through unchanged.
 * Functions and ``undefined`` are not expected in PostHog payloads but
 * are returned as-is to be safe.
 */
function redactValue(value: unknown, personBag: boolean): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    return truncateString(stripQueryAndFragment(value));
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item, personBag));
  }
  if (value instanceof Date) return value;
  if (typeof value === "object") {
    return redactObject(value as Record<string, unknown>, personBag);
  }
  return value;
}

/**
 * Walk a property bag, dropping forbidden keys and redacting the
 * remaining values. Order matters: forbidden-key filter runs FIRST so
 * we never recurse into a value we're about to drop.
 *
 * ``personBag`` relaxes the forbidden-key filter to the person-property
 * allowlist: inside a ``$set`` / ``$group_set`` bag, identity keys
 * (``email`` / ``name`` / ``full_name``) are kept because they BELONG on
 * the Person/Group, while secrets stay stripped. The flag latches on
 * once we descend into a person-property bag key, so nested objects
 * inside ``$set`` inherit it.
 */
function redactObject(
  bag: Record<string, unknown>,
  personBag: boolean,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(bag)) {
    // PostHog internal keys (e.g. properties.token = api_key) survive
    // the forbidden-property fence — see POSTHOG_INTERNAL_PROPERTY_KEYS
    // for the rationale.
    if (POSTHOG_INTERNAL_PROPERTY_KEYS.has(key)) {
      out[key] = value;
      continue;
    }
    // In a person/group property bag, an identity key on the allowlist
    // is permitted even though it is forbidden as an Event Property.
    const droppable =
      isForbiddenProperty(key) && !(personBag && isPersonPropertyAllowed(key));
    if (droppable) continue;
    // Descending into a person-property bag (``$set`` etc.) latches the
    // relaxed filter for that whole subtree.
    out[key] = redactValue(value, personBag || PERSON_PROPERTY_BAG_KEYS.has(key));
  }
  return out;
}

export function redactEvent(
  event: RedactableEvent | null,
): RedactableEvent | null {
  if (event === null) return null;
  const redacted: RedactableEvent = { ...event };
  if (event.properties) {
    // Event-level bag: starts strict; the recursion latches the
    // person-property allowlist when it descends into a nested ``$set`` /
    // ``$group_set`` (the shape posthog-js uses for $identify events).
    redacted.properties = redactObject(event.properties, false);
  }
  // Top-level person bags (when the SDK surfaces them outside
  // ``properties``) get the relaxed filter directly.
  if (event.$set) redacted.$set = redactObject(event.$set, true);
  if (event.$set_once) redacted.$set_once = redactObject(event.$set_once, true);
  return redacted;
}
